import { computed, DestroyRef, Directive, ElementRef, inject, input, OnInit, Renderer2, signal } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { NgControl } from "@angular/forms";
import { first } from "lodash-es";

// @Note: Use .form-field class as the parent node of the form control
@Directive({
  selector: "[rrUiCoreFormErrorTooltip]",
})
export class UiCoreFormErrorTooltipDirective implements OnInit {
  // DI
  readonly #ngControl = inject(NgControl, { optional: true });
  readonly #el = inject(ElementRef);
  readonly #renderer = inject(Renderer2);
  readonly #destroyRef = inject(DestroyRef);

  // I/O
  // Reason: Easier to see related custom directive I/O
  // eslint-disable-next-line  @angular-eslint/no-input-rename
  errorMesages = input<object | null>(null, { alias: "rrUiCoreFormErrorTooltipMessages" });

  // Template variables
  defaultErrorMessages = signal<{ [key: string]: string }>({
    required: "This field is required",
    noWhiteSpace: "Only whitespace is not allowed",
    email: "Invalid email format",
    default: "Invalid input",
  });
  readonly #control = computed(() => this.#ngControl?.control || null);

  ngOnInit(): void {
    this.addErrorTooltip();
  }

  private addErrorTooltip() {
    if (!this.#control()) {
      throw Error("Form control not found");
    }
    if (!this.#el || (this.#el && !this.#el.nativeElement)) {
      throw Error("Element not found");
    }

    let tooltipElement: HTMLSpanElement | null = null;
    this.#control()
      ?.statusChanges.pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe((status) => {
        if (status.toLowerCase() === "invalid" && this.#control()?.dirty && !this.#control()?.disabled) {
          if (!tooltipElement) {
            tooltipElement = this.createTooltipElement();
          } else {
            this.updateTooltipElement(tooltipElement);
          }
        } else {
          if (tooltipElement) {
            this.removeTooltipElement(tooltipElement);
          }
        }
      });
  }

  private createTooltipElement() {
    const spanEl = this.#renderer.createElement("span") as HTMLSpanElement;
    spanEl.className = "error-tooltip";
    spanEl.textContent = this.getErrorMessage();
    this.#renderer.insertBefore(this.#el.nativeElement.parentNode, spanEl, this.#el.nativeElement.nextSibling);
    return spanEl;
  }

  private updateTooltipElement(tooltipElement: HTMLSpanElement) {
    tooltipElement.textContent = this.getErrorMessage();
    this.#renderer.addClass(tooltipElement, "error-tooltip");
  }

  private removeTooltipElement(tooltipElement: HTMLSpanElement) {
    tooltipElement.textContent = "";
    this.#renderer.removeClass(tooltipElement, "error-tooltip");
  }

  private getErrorMessage(): string {
    if (!this.#control()?.errors) return "";

    const updatedMesaages = { ...this.defaultErrorMessages(), ...(this.errorMesages() ?? {}) };

    const keys = Object.keys(this.#control()!.errors ?? []);
    const errorKey: string = keys && keys.length > 0 ? first(keys)! : "";
    if (errorKey && updatedMesaages[errorKey]) {
      return updatedMesaages[errorKey];
    }

    return updatedMesaages["default"] ?? "";
  }
}
